import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProductByQRcode } from "services/order.services";
import { useQuery } from "@tanstack/react-query";
import ArticleCarousel from "./Carousel";
import CardChipButtonTwo from "components/UI/CardChipButtonTwo";
import { formatLocation } from "utils/helper";
import {
  CottonIcon,
  PolyesterIcon,
  ElastaneIcon,
  BambooIcon,
  ArrowRight,
  LightLogo,
  ArrowUpRight,
  SilkIcon,
  WoolIcon,
  LinenIcon,
  RayonIcon,
} from "assests";
import Map from "./Map";
import WashingMap from "./WashingMap";
import placeholder from "./placeholder.json";
import TransparencyCarousel from "./TransparencyCarousel";

export default function ProductPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["product", id],
    queryFn: () => getProductByQRcode(id),
    enabled: !!id,
    retry: false,
  });

  // Redirect if ID is missing
  useEffect(() => {
    if (!id) {
      navigate("/404", { replace: true });
    }
  }, [id, navigate]);

  // Redirect on error
  useEffect(() => {
    if (isError) {
      navigate("/404", { replace: true });
    }
  }, [isError, navigate]);

  // Set order state once data is available
  useEffect(() => {
    if (data?.data?.order) {
      setOrder(data.data.order);
    }
  }, [data]);

  if (isLoading || !order)
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <div className="animate-bounce">
          <LightLogo className="w-16 h-16" />
        </div>
      </div>
    );

  return (
    <div className="py-8 md:py-24 bg-[#ffffff]">
      <div className="text-center md:text-left text-[42px] font-bold w-full capitalize md:ps-[252px] pb-8 text-[#ec6423]">
        {order.article.brand ?? ""}
      </div>
      <div className="px-8 md:px-20">
        <div className="w-full flex flex-row gap-8 bg-transparent">
          <div className="w-[140px] h-[140px] p-[8.75px] rounded-[5px] border border-[#E4E7EC] hidden md:block">
            <img src={order.qr} alt="qr-code" className="w-full h-full object-contain" />
          </div>
          <div className="flex flex-col w-full max-w-[780px]">
            <div className="text-base font-bold text-[#11C182] md:text-[#101828] leading-6">
              {order.article.article_id}
            </div>
            <div className="text-5xl font-semibold text-[#101828] md:text-[#11C182] pt-3 pb-6 leading-[60px]">
              {order.article.article_title}
            </div>
            <div className="text-xl font-normal text-[#475467] leading-[30px] hidden md:block">
              {order.article.description}
            </div>
          </div>
        </div>
      </div>
      <div className="pt-4 md:pt-16 w-full ps-8 md:ps-20">
        <ArticleCarousel imagePreviews={order.article?.file} />
      </div>
      <div className="text-xl font-normal text-[#475467] leading-[30px] block md:hidden mt-4 mb-8 px-8">
        {order.article.description}
      </div>
      <div className="w-full md:w-[720px] mx-auto px-4 md:px-0">
        <div className="col-span-full md:col-span-6 md:col-start-4 flex flex-col">
          {/**Materials */}
          <div>
            <Header text="Materials" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 pt-8">
              {order.article.compositions.map((material, index) => (
                <Material key={index} type={material.name} percentage={material.percentage} />
              ))}
            </div>
          </div>
          <hr className="my-8 border-t border-gray-300" />
          {/** Traceability*/}
          <div>
            <Header text="Traceability" />
            <div className="flex justify-center md:justify-start items-center gap-2 mt-4 mb-8">
              <CardChipButtonTwo>
                {order.supplier
                  ? formatLocation({
                      region: order.supplier?.region,
                      country: order.supplier?.country,
                    })
                  : order.company && order.company?.purchased_package === "supplier"
                    ? formatLocation({
                        region: order.company?.region,
                        country: order.company?.country,
                      })
                    : order.workspace && order.workspace?.purchased_package === "supplier"
                      ? formatLocation({
                          region: order.workspace?.region,
                          country: order.workspace?.country,
                        })
                      : "N/A"}
              </CardChipButtonTwo>
              <div>
                <ArrowRight width={20} height={20} fillColor={"var(--color-global-title-3)"} />
              </div>
              <CardChipButtonTwo>
                {order.company &&
                (order.company?.purchased_package === "retailer" ||
                  order.company?.purchased_package === "importer" ||
                  order.company?.purchased_package === "brand")
                  ? formatLocation({
                      region: order.company?.region,
                      country: order.company?.country,
                    })
                  : order.workspace &&
                      (order.workspace?.purchased_package === "retailer" ||
                        order.workspace?.purchased_package === "importer" ||
                        order.workspace?.purchased_package === "brand")
                    ? formatLocation({
                        region: order.workspace?.region,
                        country: order.workspace?.country,
                      })
                    : "N/A"}
              </CardChipButtonTwo>
            </div>
            <div className="flex flex-col lg:flex-row gap-4 md:gap-6">
              <div className="w-full">
                <Map orderDetailsData={order} refetch={() => console.log("refetch")} />
              </div>
            </div>
          </div>
          {/**Transperancy */}
          <div className="mt-8">
            {/* Grid for medium and up */}
            <div className="hidden md:grid grid-cols-1 md:grid-cols-2 gap-16 pt-8">
              {transparencyFormatter(order).map((transItem, idx) => (
                <Transperancy
                  key={idx}
                  type={transItem.type}
                  company={transItem.company}
                  address={transItem.address}
                />
              ))}
            </div>
            {/* Carousel for small devices */}
            <div className="block md:hidden pt-8">
              <TransparencyCarousel>
                {transparencyFormatter(order).map((item, index) => (
                  <Transperancy
                    key={index}
                    type={item.type}
                    company={item.company}
                    address={item.address}
                  />
                ))}
              </TransparencyCarousel>
            </div>
          </div>
          <hr className="my-8 border-t border-gray-300" />
          {/**Recyclability */}
          <div>
            <Header text="Recyclability" />
            <Paragraph text="By properly maintaining your clothes, your garment will last longer. Deliver the clothing and household textiles that you no longer want to use to a recycling point. This means it can still be reused or recycled." />
            <LabeledIcon text="Take Care of your clothes" />
            <div className="mb-8">
              <img
                src={order.article?.file[0]}
                alt="picture"
                className="w-full h-[320px] md:h-[720px] rounded-lg object-cover"
              />
            </div>
          </div>
          <hr className="my-8 border-t border-gray-300" />
          {/**Washing instructions */}
          <div>
            <Header>
              <div className="inline-flex gap-2">
                <span>Washcare </span>
                <span className="hidden md:block">Instructions</span>
              </div>
            </Header>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-16 pt-8">
              {order.article.washing_instructions.map((washIns, idx) => (
                <WashingInstructions
                  key={idx}
                  icon={washIns.icon}
                  description={washIns.description}
                />
              ))}
            </div>
          </div>
          <hr className="my-8 border-t border-gray-300" />
          {/** Wear the Journey */}
          <div className="bg-[#F9FAFB] rounded-lg p-8">
            <Header text="Wear the Journey" />
            <Paragraph
              className="text-xl text-[#475467] font-normal leading-[30px] mt-8 mb-4 text-center md:text-left"
              text={`"By choosing brands that partner with ${process.env.REACT_APP_BRAND_NAME}™, you’re not just purchasing clothes; you’re becoming part of a movement towards conscious fashion. Each garment you wear is a statement—a declaration that you care about the origins of your clothes and their impact on the world.`}
            />
            <LabeledIcon text="Discover Brands that Care" />
          </div>
          <hr className="my-8 border-t border-gray-300" />
          {/**Powered by */}
          <div className="flex flex-col md:flex-row flex-nowrap items-center justify-center mt-0 md:mt-8 gap-3">
            <div className="text-xl text-[#475467] font-medium leading-[30px]">Powered by :</div>
            <div>
              <LightLogo />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Header = ({ text, children }) => (
  <div className="text-3xl font-semibold text-[#181D27] md:text-[#101828] leading-[38px] text-center md:text-left">
    {text}
    {children}
  </div>
);

const getIconComponent = (iconName) => {
  switch (iconName) {
    case "CottonIcon":
      return <CottonIcon />;
    case "PolyesterIcon":
      return <PolyesterIcon />;
    case "ElastaneIcon":
      return <ElastaneIcon />;
    case "BambooIcon":
      return <BambooIcon />;
    case "SilkIcon":
      return <SilkIcon />;
    case "WoolIcon":
      return <WoolIcon />;
    case "LinenIcon":
      return <LinenIcon />;
    case "RayonIcon":
      return <RayonIcon />;
    default:
      return <CottonIcon />;
  }
};
const Material = ({ type = "", percentage = 0 }) => {
  const key = type.toLowerCase();
  const materialsMap = placeholder.materials;
  const material = materialsMap[key];
  if (!material) return null;
  return (
    <div className="flex flex-col gap-4">
      <div className="w-full flex flex-col md:flex-row items-center gap-4">
        <div className="w-10 h-10 rounded-[6px] border border-[#D0D5DD] p-2 flex items-center justify-center">
          {getIconComponent(material.icon)}
        </div>
        <div>
          <div className="text-xl font-semibold text-[#181D27] leading-[30px] capitalize">
            {`${percentage}% ${type}`}
          </div>
        </div>
      </div>
      <div className="text-base font-normal text-[#535862] leading-[24px] text-center md:text-left">
        {material.description}
      </div>
    </div>
  );
};

const locationExtractor = (order, id, type) => {
  const documents = order.documents || [];

  for (const document of documents) {
    const target = document[type];
    if (target && target.id === id) {
      return `${target.region || ""}${target.region && target.country ? ", " : ""}${target.country || ""}`;
    }
  }
  return null;
};
const transparencyFormatter = (order) => {
  const data = [];
  let transparencies = order.transparency;

  for (let i = 0; i <= 4; i++) {
    const tierKey = `tier_${i}`;
    const tierItems = transparencies[tierKey] || [];

    tierItems.forEach(({ id, name }) => {
      const supplier = transparencies[`${tierKey}_suppliers`]?.[id]?.[0];
      const subSupplier = transparencies[`${tierKey}_sub_suppliers`]?.[id]?.[0];
      const company = subSupplier?.name || supplier?.name || null;
      const address =
        locationExtractor(order, subSupplier?.id, "sub_supplier") ||
        locationExtractor(order, supplier?.id, "supplier") ||
        null;

      data.push({
        type: name.toLowerCase(),
        company,
        address,
      });
    });
  }
  return data;
};

const Transperancy = ({ type, company, address }) => {
  const key = type;
  const transparencyMaps = placeholder.transparency;
  const description = transparencyMaps[key];

  if (!description) return null;
  return (
    <div>
      <div className="text-xl text-[#181D27] font-semibold leading-[30px] capitalize mb-2">
        {type}
      </div>
      <div className="text-base text-[#181D27] font-medium leading-[24px] capitalize mb-2">
        {company}
      </div>
      <div className="text-base text-[#535862] font-medium leading-[24px] capitalize mb-4">
        {description}
      </div>
      {address && (
        <div className="w-fit h-[22px] border border-[#D0D5DD] rounded-[6px] text-xs text-[#344054] font-medium leading-[18px] capitalize px-[6px] py-[2px]">
          {address}
        </div>
      )}
    </div>
  );
};

const Paragraph = ({
  className = "text-base text-[#535862] font-normal leading-6 mt-8 mb-4 text-center md:text-left",
  text,
}) => <div className={className}>{text}</div>;

const LabeledIcon = ({ text }) => (
  <div className="text-base text-[#11C182] font-semibold leading-8 mb-8 flex flex-row flex-nowrap items-center justify-center md:justify-start gap-2">
    <span>{text}</span>
    <span>
      <ArrowUpRight width={20} height={20} fillColor={"#11C182"} />
    </span>
  </div>
);

const WashingInstructions = ({ icon = "", description }) => {
  const washInstructionMaps = placeholder.washInstructions;
  const [, num] = icon.split("_");
  const number = Number(num);
  const WashingIcon = WashingMap[number];
  const title = washInstructionMaps[number];

  if (!WashingIcon) return null;

  return (
    <div className="flex flex-col gap-4">
      <div className="w-full flex flex-col md:flex-row items-center gap-4">
        <div className="w-10 h-10 rounded-[6px] border border-[#D0D5DD] p-2 flex items-center justify-center">
          <WashingIcon />
        </div>
        <div>
          <div className="text-xl font-semibold text-[#181D27] leading-[30px] capitalize">
            {title}
          </div>
        </div>
      </div>
      <div className="text-base font-normal text-[#535862] leading-[24px] text-center md:text-left">
        {description}
      </div>
    </div>
  );
};
